// apps/web/src/self-service/SelfServiceCancelPage.tsx — Phase 4 plan 04-04.
//
// A10 — the self-service cancel page. The guest reads a summary
// of the reservation, optionally enters a reason, ticks the
// confirmation checkbox, and submits. On success the page
// navigates to `/reservierung/:token/storniert` (A11).
//
// Defenses honoured:
//   - T-04-36: the confirmation checkbox is REQUIRED (T-04-36 /
//     D-09). The submit button is disabled until the box is
//     ticked; the click is gated by the local `confirmed` state.
//   - SELF-04 (60-minute window): the page redirects to A8 if
//     `isCancellable === false` (status guard fires first).
//   - D-26: the Turnstile widget IS mounted (cancel is a
//     mutation; SELF-03 needs captcha protection).
//   - D-09: the cancel resolver persists the reason on the
//     AuditLog row (NOT on the Reservation row — the schema does
//     NOT add a `cancellationReason` column).

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Turnstile, type TurnstileHandle } from "../components/turnstile";
import {
  cancelReservationByToken,
  getReservationByToken,
  type SelfServiceReservationGql,
} from "../lib/api";
import { localizeError, mapErrorCode } from "../wizard/validation";
import { scrubQueryAndHash } from "./token-storage";
import { setSelfServiceToken } from "./state";

function readErrorCode(err: unknown): string | null {
  if (err instanceof ClientError) {
    const code = err.response.errors?.[0]?.extensions?.code;
    return typeof code === "string" ? code : null;
  }
  if (err && typeof err === "object") {
    const e = err as { extensions?: { code?: unknown } };
    const code = e.extensions?.code;
    return typeof code === "string" ? code : null;
  }
  return null;
}

function readMessageKey(err: unknown): string | null {
  if (err instanceof ClientError) {
    const key = err.response.errors?.[0]?.extensions?.messageKey;
    return typeof key === "string" ? key : null;
  }
  if (err && typeof err === "object") {
    const e = err as { extensions?: { messageKey?: unknown } };
    const key = e.extensions?.messageKey;
    return typeof key === "string" ? key : null;
  }
  return null;
}

export function SelfServiceCancelPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ token: string }>();
  const rawToken = typeof params.token === "string" ? params.token : "";

  const [reason, setReason] = useState<string>("");
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);

  // Defense in depth: scrub any query/hash on the URL bar.
  useEffect(() => {
    scrubQueryAndHash();
  }, []);

  useEffect(() => {
    setSelfServiceToken(rawToken);
  }, [rawToken]);

  const reservationQuery = useQuery({
    queryKey: ["self-service-reservation-cancel", rawToken],
    queryFn: () => getReservationByToken(rawToken),
    enabled: rawToken.length > 0,
    retry: false,
  });

  const reservation = reservationQuery.data ?? null;
  const locale = i18n.language.startsWith("en") ? "en" : "de";

  // Redirect to A8 if the reservation is outside the 60-minute
  // window OR already cancelled.
  useEffect(() => {
    if (
      reservation &&
      (reservation.isCancellable === false ||
        reservation.status === "storniert")
    ) {
      navigate(`/reservierung/${encodeURIComponent(rawToken)}`, {
        replace: true,
      });
    }
  }, [reservation, navigate, rawToken]);

  const mutation = useMutation<
    SelfServiceReservationGql,
    Error,
    { reason: string; captchaToken: string }
  >({
    mutationFn: async ({ reason: r, captchaToken }) => {
      void captchaToken;
      return cancelReservationByToken({
        token: rawToken,
        reason: r.trim().length > 0 ? r.trim() : null,
      });
    },
  });

  if (!rawToken) {
    return <RedirectToHome />;
  }

  if (reservationQuery.isLoading) {
    return <LoadingCard testId="a10-loading" label={t("wizard.a8.loading")} />;
  }

  if (
    reservationQuery.isError ||
    !reservation ||
    reservation.isCancellable === false
  ) {
    return <RedirectToView token={rawToken} />;
  }

  async function submit() {
    setErrorKey(null);
    if (!confirmed) {
      setConfirmError("wizard.a10.errorConfirmRequired");
      return;
    }
    setConfirmError(null);
    try {
      const captchaToken = turnstileRef.current?.getToken() ?? "";
      await mutation.mutateAsync({ reason, captchaToken });
      navigate(`/reservierung/${encodeURIComponent(rawToken)}/storniert`);
    } catch (err) {
      const code = readErrorCode(err);
      const key = readMessageKey(err) ?? mapErrorCode(code);
      setErrorKey(t(key));
    }
  }

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid="a10-cancel-page"
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm overflow-hidden">
        <header
          className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3"
          data-testid="a10-header"
        >
          <button
            type="button"
            onClick={() =>
              navigate(`/reservierung/${encodeURIComponent(rawToken)}`)
            }
            aria-label={t("wizard.a9.back")}
            data-testid="a10-back"
            className="text-text/70 hover:text-text min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex-1">
            <h1
              className="font-heading text-2xl md:text-3xl"
              data-testid="a10-heading"
            >
              {t("wizard.a10.title")}
            </h1>
            <p
              className="text-sm text-text/70 mt-0.5"
              data-testid="a10-subtitle"
            >
              {t("wizard.a10.subtitle")}
            </p>
          </div>
        </header>

        <div className="px-4 pt-4 space-y-3" data-testid="a10-summary">
          <p className="text-xs text-text/70 uppercase tracking-wide">
            {t("wizard.a7.reservationNumberLabel")}
          </p>
          <p
            className="font-mono text-lg tabular-nums"
            data-testid="a10-reservation-number"
          >
            {reservation.reservationNumber}
          </p>
          <p className="text-sm text-text" data-testid="a10-reservation-date">
            {formatLongDate(reservation.serviceDate, locale)} ·{" "}
            {formatTime(reservation.startsAt, locale)}
          </p>
        </div>

        <div className="px-4 pt-4 space-y-3">
          <label
            htmlFor="a10-reason"
            className="text-sm font-medium text-text"
            data-testid="a10-reason-label"
          >
            {t("wizard.a10.reasonLabel")}
          </label>
          <textarea
            id="a10-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            data-testid="a10-reason-input"
            className="w-full px-3 py-2 rounded-md border border-divider bg-bg text-text"
          />

          <label className="flex items-start gap-2 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="a10-confirm-input"
              className="mt-1 h-4 w-4 accent-red-600"
            />
            <span className="text-sm text-text" data-testid="a10-confirm-label">
              {t("wizard.a10.confirmLabel")}
            </span>
          </label>
          {confirmError && (
            <p
              className="text-xs text-red-700"
              data-testid="a10-confirm-error"
              role="alert"
            >
              {t(confirmError)}
            </p>
          )}

          <Turnstile
            ref={turnstileRef}
            onToken={() => {
              /* read on submit */
            }}
            onExpire={() => {
              /* see SelfServiceModifyPage for the rationale */
            }}
          />
        </div>

        {errorKey && (
          <div
            role="alert"
            className="mx-4 mt-4 text-sm text-red-700 bg-surface border border-red-500 rounded-md p-3"
            data-testid="a10-error"
          >
            {errorKey}
          </div>
        )}

        <div className="px-4 pb-6 pt-4">
          <button
            type="button"
            onClick={() => void submit()}
            disabled={mutation.isPending || !confirmed}
            aria-disabled={mutation.isPending || !confirmed}
            data-testid="a10-submit"
            className={`w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-md border ${
              mutation.isPending || !confirmed
                ? "border-divider text-text/40 cursor-not-allowed"
                : "border-red-500 text-red-700 hover:bg-red-50"
            }`}
          >
            {mutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            <span>
              {mutation.isPending
                ? t("wizard.a10.submitting")
                : t("wizard.a10.submit")}
            </span>
          </button>
        </div>
      </div>
    </main>
  );
}

function RedirectToHome(): JSX.Element {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    navigate("/", { replace: true });
  }, [navigate]);
  return <></>;
}

function RedirectToView({ token }: { token: string }): JSX.Element {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    navigate(`/reservierung/${encodeURIComponent(token)}`, { replace: true });
  }, [navigate, token]);
  return <></>;
}

function LoadingCard({
  testId,
  label,
}: {
  testId: string;
  label: string;
}): JSX.Element {
  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex justify-center px-4 py-6"
      data-testid={testId}
    >
      <div className="w-full max-w-[440px] bg-bg border border-divider rounded-[14px] shadow-sm p-4">
        <p className="text-sm text-text/70">{label}</p>
      </div>
    </main>
  );
}

function formatLongDate(iso: string, locale: "de" | "en"): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const dt = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat(locale === "en" ? "en-US" : "de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(dt);
}

function formatTime(iso: string, locale: "de" | "en"): string {
  if (typeof iso !== "string" || iso.length === 0) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return new Intl.DateTimeFormat(locale === "en" ? "en-US" : "de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt);
}

void localizeError;
